import {
  getB2CUserCulture,
  getB2CUserCurrency,
  getUserLanguage,
} from '@app/utils'

import {
  getPostAdPath,
  getProfileAvatarUrl,
  getProfileEmail,
  getProfileUsername,
} from './constants'
import { isEstimateEnabled } from './isEstimateEnabled'
import { isPostAdEnabled } from './isPostAdEnabled'
import { BASE_B2C_NAVIGATION_MODEL } from './navigationModel'
import {
  buildFlagPath,
  getProfileLabel,
  resolveLocalizedLabel,
} from './profilePresentation'
import { hasStandardOrHigherInOtherAccounts } from './profileRoleVisibility'
import { buildB2CProfileSubmenu } from './profileSubmenu'
import { getB2CProfileUrl, getProfessionalsDashboardUrl } from './profileUrls'
import type { NavigationData } from '../navigationModel'
import type { BuildB2CNavigationModelOptions, NavigationItem } from './types'

export const buildB2CNavigationModel = ({
  userState,
  isWebView,
  unreadTotal,
}: BuildB2CNavigationModelOptions): NavigationData => {
  const avatarUrl = getProfileAvatarUrl(userState)
  const username = getProfileUsername(userState)
  const email = getProfileEmail(userState)
  const b2cCulture = getB2CUserCulture()?.replace(/_/g, '-')
  const [cultureLanguageCode = '', cultureCountryCode = ''] =
    b2cCulture?.split('-') || []
  const b2cCurrency = getB2CUserCurrency() || undefined

  const languageCode =
    cultureLanguageCode ||
    userState.currentUser.regionalSettings?.language ||
    userState.currentUser.mainLanguage ||
    ''
  const countryCode =
    cultureCountryCode ||
    userState.currentUser.regionalSettings?.countryISO ||
    userState.currentUser.countryISO ||
    ''
  const currency =
    b2cCurrency ||
    userState.currentUser.regionalSettings?.currencyId ||
    userState.currentUser.currencyId ||
    ''

  const normalizedLanguageCode = languageCode.toLowerCase()
  const normalizedCountryCode = countryCode.toUpperCase()
  const estimateEnabled = isEstimateEnabled(normalizedCountryCode)
  const postAdEnabled = isPostAdEnabled(normalizedCountryCode)
  const profileLocale = normalizedLanguageCode || 'en'
  const postAdHref = getPostAdPath(profileLocale)

  const myProfileHref = getB2CProfileUrl('/my-profile/', email)
  const regionalSettingsMenuHref = getB2CProfileUrl(
    '/my-profile/settings',
    email
  )
  const regionalSettingsHref =
    BASE_B2C_NAVIGATION_MODEL.profile?.regionalSettingsHref
  const securityHref = getB2CProfileUrl('/my-profile/security', email)
  const isStandardOrHigherForDashboard =
    hasStandardOrHigherInOtherAccounts(userState)

  const language = b2cCulture || getUserLanguage()
  const professionalsHref = getProfessionalsDashboardUrl(language)

  return {
    ...BASE_B2C_NAVIGATION_MODEL,
    profile: {
      ...BASE_B2C_NAVIGATION_MODEL.profile,
      label: getProfileLabel(username, email),
      avatarUrl,
      username,
      email,
      language: resolveLocalizedLabel(
        normalizedLanguageCode,
        'language',
        profileLocale
      ),
      country: normalizedCountryCode,
      flag: {
        x1: buildFlagPath(normalizedCountryCode, '1x'),
        x2: buildFlagPath(normalizedCountryCode, '2x'),
      },
      countryName: resolveLocalizedLabel(
        normalizedCountryCode,
        'region',
        profileLocale
      ),
      currency,
      regionalSettingsHref,
    },
    items: (BASE_B2C_NAVIGATION_MODEL.items as NavigationItem[])
      .filter((item) => estimateEnabled || item.type !== 'Estimate')
      .filter((item) => postAdEnabled || item.type !== 'PostAd')
      .map((item) => {
        if (item.type === 'PostAd') {
          return {
            ...item,
            href: postAdHref,
          }
        }

        if (item.type === 'Chat') {
          return {
            ...item,
            unread: unreadTotal,
          }
        }

        if (item.type !== 'MyProfile' || !item.submenu) {
          return item
        }

        const filteredSubmenu = buildB2CProfileSubmenu(
          item.submenu,
          {
            isStandardOrHigher: isStandardOrHigherForDashboard,
            isWebView,
          },
          {
            myProfileHref,
            regionalSettingsHref: regionalSettingsMenuHref,
            securityHref,
            professionalsHref,
          }
        )

        return {
          ...item,
          href: myProfileHref,
          submenu: filteredSubmenu,
        }
      }),
  }
}
